<template>
    <div v-if="!$store.state.Buyflow.summary.summaryError">
        <SummaryLot
            v-if="$store.state.Buyflow.summary.summary !== {}"
            :is-fmf="$store.getters['isFmf']"
            :mobile-eligible="$store.state.Buyflow.mobileEligible"
            :mobile-interested="$store.state.Buyflow.mobileInterested"
            :is-gwp="!!promoGiftcard"
            :gwp-amount="promoGiftcard || null"
            :first-name="$store.state.meta.firstName || ''"
            :fmf-amount="$store.getters['fmfAmount'] || 0"
            :summary="$store.state.Buyflow.summary.summary"
            :cart-data="$store.state.Buyflow.receipt.cartData"
            :discount-amount="$store.getters['discountAmount']"
            :is-fiber="$store.getters['Buyflow/internet/isFiber']"
            :is-wow-market="$store.getters['Buyflow/isWowMarket']"
            :is-tech-delivered="$store.getters['Buyflow/finalize/isTechDelivered']"
            :format-install-date="$store.getters['Buyflow/summary/formatInstallDate']()"
            :is-equipment-selected="$store.getters['Buyflow/internet/isEquipmentSelected']"
            :has-selected-self-install="$store.getters['Buyflow/finalize/hasSelectedSelfInstall']"
        />
    </div>
</template>

<script>
    import SummaryLot from '~/Buyflow/components/SummaryLot'
    import products from "../../../templates/products/_products";
    import {mapGetters, mapState} from "vuex";

    export default {
        name: 'SummaryPage',
        components: {
            SummaryLot,
        },
        props: {
            productMetadata: {
                type: String,
                required: false,
                default: "[]",
            },
        },
        mounted () {
            const url = window.location.href
            const body = document.querySelector('body')

            if (url.includes('checkout/summary')) {
                body.classList.add('buyflow-summary')
                
                // Check if summary data is available
                if (this.$store.state.Buyflow.summary.summary?.id) {
                    this.sendPurchaseEvent();
                } else {
                    // wait for data
                    this.$watch(
                        () => this.$store.state.Buyflow.summary.summary?.id,
                        (newValue) => {
                            if (newValue) {
                                console.log('Transaction ID now available:', newValue);
                                this.sendPurchaseEvent();
                            }
                        }
                    );
                }
            }
        },
      computed: {
        ...mapState({
          internetName: state => state.Buyflow.summary.summary?.receipt?.service?.internet[0]?.name,
          ecommerceItem: state => state.ecommerceItem,
          cartData: state => state.Buyflow.receipt.cartData,
        }),
        ...mapGetters({
          filterAndMerge: 'filterAndMerge',
          parseMetadata: 'parseMetadata',
        }),
        productData () {
          return {
            file: this.parseMetadata(products ?? []),
            craft: this.parseMetadata(JSON.parse(this.productMetadata) ?? [])
          }
        },
        mergedMetadata() {
          return this.filterAndMerge({base: this.productData.file, override: this.productData.craft})
        },
        promoGiftcard(){
          if (!this.mergedMetadata) return "";
          if (!this.internetName) return "";
          if (!this.mergedMetadata[this.internetName]) return "";

          const data =  this.mergedMetadata[this.internetName][0]
          const price = data?.variants?.customizations?.internet?.products[this.internetName]?.banner?.price;
          return price ? price + ' Promo Gift Card' :  ''
        }
      },
    methods: {
        sendPurchaseEvent() {
          this.pushEvent('purchase', {
              'event_label': this.$store.getters['Buyflow/tv/hasSelectedAService'], // TV plan added
              'event_label_2': this.$store.getters['Buyflow/voice/hasSelected'](), // Home Phone added
              'event_label_3': this.$store.state.Buyflow.mobileInterested ? 'added' : (this.$store.state.Buyflow.mobileEligible ? 'eligible' : 'null'), // Added/Eligible
              'event_label_4': this.$store.getters['Buyflow/internet/isEquipmentSelected']({ name: 'Customer Owned Modem' }) ? 'true' : 'false', // Own Equipment selected
              'value': this.cartData?.promoPriceTotal || 0, // Total cart value from cart data
              'currency': 'USD',
              'transaction_id': this.$store.state.Buyflow.summary.summary?.id || '', // Order ID
              'flow_step_name': 'confirmation',
              'flow_step_number': '5',
              'items': this.getCartItems() // Cart items
          });
        },
        getCartItems() {
            const items = [];
            const cartData = this.cartData;
            
            if (!cartData) {
                return [];
            }
            
            // Add Internet service
            if (cartData.services?.internet?.length) {
                cartData.services.internet.forEach(item => {
                    items.push({
                        item_id: item.name,
                        item_name: item.name,
                        item_category: 'internet',
                        item_brand: 'Breezeline Online',
                        price: item.promoPrice,
                        quantity: 1
                    });
                });
            }
            
            // Add Internet equipment
            if (cartData.addOns?.internet?.equipment?.length) {
                cartData.addOns.internet.equipment.forEach(item => {
                    items.push({
                        item_id: item.name,
                        item_name: item.name,
                        item_category: 'internet-equipment',
                        item_brand: 'Breezeline Online',
                        price: item.promoPrice,
                        quantity: item.quantity
                    });
                });
            }
            
            // Add TV service
            if (cartData.services?.tv?.length) {
                cartData.services.tv.forEach(item => {
                    items.push({
                        item_id: item.name,
                        item_name: item.name,
                        item_category: 'tv',
                        item_brand: 'Breezeline Online',
                        price: item.promoPrice,
                        quantity: 1
                    });
                });
            }
            
            // Add TV packages
            if (cartData.addOns?.tv?.packages?.length) {
                cartData.addOns.tv.packages.forEach(item => {
                    items.push({
                        item_id: item.name,
                        item_name: item.name,
                        item_category: 'tv-package',
                        item_brand: 'Breezeline Online',
                        price: item.promoPrice,
                        quantity: item.quantity
                    });
                });
            }
            
            // Add TV base options
            if (cartData.addOns?.tv?.base?.length) {
                cartData.addOns.tv.base.forEach(item => {
                    items.push({
                        item_id: item.name,
                        item_name: item.name,
                        item_category: 'tv-base',
                        item_brand: 'Breezeline Online',
                        price: item.promoPrice,
                        quantity: item.quantity
                    });
                });
            }
            
            // Add TV equipment
            if (cartData.addOns?.tv?.equipment?.length) {
                cartData.addOns.tv.equipment.forEach(item => {
                    items.push({
                        item_id: item.name,
                        item_name: item.name,
                        item_category: 'tv-equipment',
                        item_brand: 'Breezeline Online',
                        price: item.promoPrice,
                        quantity: item.quantity
                    });
                });
            }
            
            // Add Voice service
            if (cartData.services?.voice?.length) {
                cartData.services.voice.forEach(item => {
                    items.push({
                        item_id: item.name,
                        item_name: item.name,
                        item_category: 'voice',
                        item_brand: 'Breezeline Online',
                        price: item.promoPrice,
                        quantity: 1
                    });
                });
            }
            
            // Add Voice options
            if (cartData.addOns?.voice?.base?.length) {
                cartData.addOns.voice.base.forEach(item => {
                    items.push({
                        item_id: item.name,
                        item_name: item.name,
                        item_category: 'voice-base',
                        item_brand: 'Breezeline Online',
                        price: item.promoPrice,
                        quantity: item.quantity
                    });
                });
            }
            
            // Add Installation
            if (cartData.addOns?.installation?.base?.length) {
                cartData.addOns.installation.base.forEach(item => {
                    items.push({
                        item_id: item.name,
                        item_name: item.name,
                        item_category: 'installation',
                        item_brand: 'Breezeline Online',
                        price: item.promoPrice,
                        quantity: item.quantity
                    });
                });
            }
            
            return items;
        }
    }
}
</script>
